/**
 * 事件上报服务
 */

import type { EventAPI } from '../api/event'
import { getStoragePrefix } from '../config/SDKConfigManager'
import { getDeviceEnvironment, isMiniWechatWebView } from '../utils/device'

export class EventService {
  private eventAPI: EventAPI
  private hasReportedInstall: boolean = false
  private lastOnlineDate: string = ''

  constructor(eventAPI: EventAPI) {
    this.eventAPI = eventAPI
    this.loadState()
  }

  /**
   * 获取存储 key
   */
  private getStorageKey(key: string): string {
    return `${getStoragePrefix()}${key}`
  }

  /**
   * 加载状态
   */
  private loadState() {
    if (typeof localStorage === 'undefined') return

    const installStatus = localStorage.getItem(this.getStorageKey('install_status'))
    const legacyInstallStatus = localStorage.getItem('installStatus')
    this.hasReportedInstall = installStatus === '1' || legacyInstallStatus === '1'

    const onlineDate = localStorage.getItem(this.getStorageKey('online_date'))
    const legacyOnlineDate = localStorage.getItem('onlineTime')
    this.lastOnlineDate = onlineDate || legacyOnlineDate || ''
  }

  /**
   * 保存状态
   */
  private saveState() {
    if (typeof localStorage === 'undefined') return

    if (this.hasReportedInstall) {
      localStorage.setItem(this.getStorageKey('install_status'), '1')
      localStorage.setItem('installStatus', '1')
    }

    if (this.lastOnlineDate) {
      localStorage.setItem(this.getStorageKey('online_date'), this.lastOnlineDate)
      localStorage.setItem('onlineTime', this.lastOnlineDate)
    }
  }

  /**
   * 获取当前日期字符串（YYYY-MM-DD）
   */
  private getTodayString(): string {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  /**
   * 上报属于旁路链路，必须脱离初始化主流程执行。
   */
  private runDetachedReport(
    label: string,
    task: () => Promise<void>,
    onSuccess?: () => void
  ): void {
    void Promise.resolve()
      .then(task)
      .then(() => {
        onSuccess?.()
      })
      .catch(error => {
        console.error(`[EventService] Failed to report ${label} event`, error)
      })
  }

  /**
   * 初始化事件上报
   * 在 SDK 初始化时调用
   */
  async initialize(options?: {
    bdVid?: string
    strategy?: string
    enableEventReport?: boolean
    eventReport?: boolean
  }): Promise<void> {
    // 如果禁用事件上报，直接返回
    if (options?.enableEventReport === false || options?.eventReport === false) {
      return
    }

    try {
      const device = getDeviceEnvironment()

      // 1. 百度回传事件（如果有 bdVid 和 strategy）
      if (options?.bdVid && options?.strategy) {
        this.runDetachedReport('Baidu', () =>
          this.eventAPI.reportBaiduEvent({
            bdVid: options.bdVid as string,
            strategy: options.strategy as string,
            eventType: 3
          })
        )
      }

      // 2. 访问事件（非小程序环境）
      if (!isMiniWechatWebView()) {
        this.runDetachedReport('visit', () => this.eventAPI.reportVisitEvent(options?.strategy))

        // 3. 安装事件（首次安装，只有成功才标记）
        if (!this.hasReportedInstall && device !== 'desktop-app') {
          this.runDetachedReport(
            'install',
            () => this.eventAPI.reportInstallEvent(options?.strategy),
            () => {
              this.hasReportedInstall = true
              this.saveState()
            }
          )
        }

        // 4. 上线事件（每天首次，只有成功才标记）
        const today = this.getTodayString()
        if (this.lastOnlineDate !== today) {
          this.runDetachedReport(
            'online',
            () => this.eventAPI.reportOnlineEvent(options?.strategy),
            () => {
              this.lastOnlineDate = today
              this.saveState()
            }
          )
        }
      }
    } catch (error) {
      console.error('[EventService] Failed to report events', error)
      // 事件上报失败不影响主流程
    }
  }

  /**
   * 百度回传事件
   */
  async reportBaiduEvent(bdVid: string, strategy: string): Promise<void> {
    try {
      await this.eventAPI.reportBaiduEvent({
        bdVid,
        strategy,
        eventType: 3
      })
    } catch (error) {
      console.error('[EventService] Failed to report Baidu event', error)
    }
  }

  /**
   * 安装事件
   */
  async reportInstallEvent(strategy?: string): Promise<void> {
    try {
      await this.eventAPI.reportInstallEvent(strategy)
    } catch (error) {
      console.error('[EventService] Failed to report install event', error)
    }
  }

  /**
   * 上线事件
   */
  async reportOnlineEvent(strategy?: string): Promise<void> {
    try {
      await this.eventAPI.reportOnlineEvent(strategy)
    } catch (error) {
      console.error('[EventService] Failed to report online event', error)
    }
  }

  /**
   * 访问事件
   */
  async reportVisitEvent(strategy?: string): Promise<void> {
    try {
      await this.eventAPI.reportVisitEvent(strategy)
    } catch (error) {
      console.error('[EventService] Failed to report visit event', error)
    }
  }

  /**
   * 自定义事件
   */
  async reportCustomEvent(eventNum: number, strategy?: string): Promise<void> {
    try {
      await this.eventAPI.reportCustomEvent(eventNum, strategy)
    } catch (error) {
      console.error('[EventService] Failed to report custom event', error)
    }
  }
}
